import React from "react";
import PropTypes from "prop-types";
import { Typography, Divider, Paper, Container, Grid, Box } from "@mui/material";
import { getCrmDashboard, getCrmRevenue, getCrmOrdersStatusDistribution } from "../../api";
import { useAnalyticsFetch } from "./hooks/useAnalyticsFetch";
import KpiCard from "./components/KpiCard";
import LineChartCard from "./components/LineChartCard";
import DoughnutChartCard from "./components/DoughnutChartCard";
import { formatStatusLabel, getStatusColor } from "./components/orderStatusMap";

const Dashboard = ({ filter, startDate, endDate }) => {
  const { data: kpis, loading: kpisLoading } = useAnalyticsFetch(getCrmDashboard, [
    filter,
    startDate,
    endDate,
  ]);

  const { data: revenue, loading: revenueLoading } = useAnalyticsFetch(getCrmRevenue, [
    filter,
    startDate,
    endDate,
    "day",
  ]);

  const { data: statusDistribution, loading: statusLoading } = useAnalyticsFetch(
    getCrmOrdersStatusDistribution,
    [filter, startDate, endDate],
  );

  return (
    <Container sx={{ paddingLeft: "0 !important", paddingRight: "0 !important" }}>
      <Paper sx={{ padding: "1rem", marginBottom: "1rem" }}>
        <Typography
          sx={{
            fontSize: "18px",
            fontWeight: "700",
            lineHeight: "1.5em",
            marginBottom: "1rem",
          }}
        >
          Dashboard
        </Typography>
        <Divider sx={{ marginBottom: "1rem" }} />
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6} md={4}>
            <KpiCard label="Total Revenue" value={kpis?.total_revenue ?? 0} color="#17a2b8" currency loading={kpisLoading} />
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <KpiCard label="Total Orders" value={kpis?.total_orders ?? 0} color="#28a745" loading={kpisLoading} />
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <KpiCard
              label="Average Order Value"
              value={kpis?.average_order_value ?? 0}
              color="#5b841b"
              currency
              loading={kpisLoading}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <KpiCard label="Total Customers" value={kpis?.total_customers ?? 0} color="#2271b1" loading={kpisLoading} />
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <KpiCard label="New Customers" value={kpis?.new_customers ?? 0} color="#8e44ad" loading={kpisLoading} />
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <KpiCard label="Refund Amount" value={kpis?.refund_amount ?? 0} color="#c0392b" currency loading={kpisLoading} />
          </Grid>
        </Grid>
      </Paper>

      <Box sx={{ marginBottom: "1rem" }}>
        <Grid container spacing={2}>
          <Grid item xs={12} md={8}>
            <LineChartCard
              title="Revenue"
              labels={revenue?.revenue?.labels || []}
              datasets={
                revenue?.revenue
                  ? [{ label: "Revenue", data: revenue.revenue.series }]
                  : []
              }
              loading={revenueLoading}
              currency
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <DoughnutChartCard
              title="Order Status"
              labels={(statusDistribution?.distribution?.labels || []).map(formatStatusLabel)}
              series={statusDistribution?.distribution?.series || []}
              colors={(statusDistribution?.distribution?.labels || []).map((slug, index) => getStatusColor(slug, index))}
              loading={statusLoading}
            />
          </Grid>
        </Grid>
      </Box>
    </Container>
  );
};

Dashboard.propTypes = {
  filter: PropTypes.string.isRequired,
  startDate: PropTypes.string,
  endDate: PropTypes.string,
};

export default Dashboard;
